import {
  Button,
  Column,
  Copy,
  Flex,
  Row,
  Square,
  Squish,
  Stack,
  SvgX,
} from '@grubhubprod/gh-cookbook-react';
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  ComponentProps,
} from 'react';
import { debounce } from '@grubhubprod/umami-library/dist/nodash/debounce';
import { NoResultsMsg } from 'src/components/Table/NoResultsMsg';
import Table from 'src/components/Table/Table';
import {
  IGroup,
  ILocDiner,
  ISearchParams,
  SearchStatus,
  useGroupDinersSearch,
} from 'src/utilities/useSearchHooks';
import { Checkbox } from 'src/components/Checkbox/Checkbox';
import { useCheckboxSelection } from 'src/components/People/Groups/hooks/useCheckboxSelection';
import AddPeopleToLocModal from 'src/components/selfService/features/LineOfCredit/details/AddPeopleToLocModal';
import { useGroupPeopleManagement } from 'src/components/People/Groups/hooks/useGroupPeopleManagement';
import { SimpleDialog } from 'src/components/SimpleDialog/SimpleDialog';
import { useScreenSizes } from 'src/utilities/useScreenSizes';
import { useSearchToolbar } from 'src/components/People/Groups/hooks/useSearchToolbar';
import { StickyToolbarContainer } from 'src/components/People/StickyToolbarContainer';
import { GroupPeopleSearchToolbar } from 'src/components/People/Groups/GroupPeopleSearchToolbar';
import { useSendAnalytics } from 'src/components/AnalyticsProvider/useSendAnalytics';

const DEBOUNCE_SEARCH_TIMING = 300;

type GroupPeopleTableProps = {
  corpId: string;
  groupData: IGroup;
};
export const GroupPeopleTable: React.FC<GroupPeopleTableProps> = ({
  corpId,
  groupData,
}) => {
  const { isDesktop } = useScreenSizes();
  const [isAddPeopleVisible, setIsAddPeopleVisible] = useState(false);
  const [isConfirmRemovePeopleVisible, setIsConfirmRemovePeopleVisible] =
    useState(false);
  const [selectedPersonToRemove, setSelectedPersonToRemove] =
    useState<ILocDiner>(null);

  const { id: groupId, name: groupName } = groupData;

  const { sendAnalyticsBefore } = useSendAnalytics({
    eventcategory: 'self-service management tool_loc',
    eventaction: 'add people to group_cta',
  });

  const {
    doSearch: searchGroupDiners,
    totalResults,
    resultsList,
    loadMoreNumber,
    searchStatus,
    hasNoResultsWithoutQuery,
    hasNoResultsWithQuery,
  } = useGroupDinersSearch(corpId, groupId);

  const currentSearchTermRef = useRef<string>('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { isToolbarVisible } = useSearchToolbar({
    callbackBeforeToggle: () => {
      currentSearchTermRef.current = searchInputRef.current?.value;
    },
    callbackAfterToggle: () => {
      if (searchInputRef.current)
        searchInputRef.current.value = currentSearchTermRef?.current || '';
    },
  });

  useEffect(() => {
    if (corpId) {
      doSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpId]);

  function doSearch(searchParams: ISearchParams = {}, isLoadMore?: boolean) {
    const query = searchInputRef.current?.value;
    const params: ISearchParams = { ...searchParams };

    if (query) {
      params.query = query;
    }

    return searchGroupDiners(params, isLoadMore);
  }

  const handleLoadMoreClick = () => doSearch({}, true);

  const debouncedSearch = debounce(doSearch, DEBOUNCE_SEARCH_TIMING);

  function onSearchGroupInput() {
    debouncedSearch();
  }

  const {
    toggleItem,
    toggleAll,
    isSelected,
    numberOfSelectedItems,
    selectedItems,
  } = useCheckboxSelection(resultsList, 'id');

  const { isLoading: isAddPeopleToGroupLoading, mutate: addPeopleToGroup } =
    useGroupPeopleManagement({
      action: 'add',
      corpId,
      groupId,
      onSuccess: () => {
        sendAnalyticsBefore(doSearch);
      },
    });

  const {
    isLoading: isRemovePeopleFromGroupLoading,
    mutate: removePeopleFromGroup,
  } = useGroupPeopleManagement({
    action: 'remove',
    corpId,
    groupId,
    onSuccess: () => {
      toggleAll(false);
      setIsConfirmRemovePeopleVisible(false);
      setSelectedPersonToRemove(null);
      doSearch();
    },
  });

  const handleBulkRemoveClick = () => {
    if (numberOfSelectedItems > 1) {
      setIsConfirmRemovePeopleVisible(true);
    } else {
      const foundPerson = resultsList.find(({ id }) => id === selectedItems[0]);
      if (foundPerson) {
        setSelectedPersonToRemove(foundPerson);
      }
    }
  };

  const simpleDialogDefaultProps = useMemo(
    () => ({
      primaryActionFlavor: 'danger',
      primaryActionText: 'Remove',
      isPrimaryButtonLoading: isRemovePeopleFromGroupLoading,
      buttonsDirection: 'row-reverse' as ComponentProps<
        typeof SimpleDialog
      >['buttonsDirection'],
      secondaryActionText: 'Cancel',
      disablePrimaryActionButton: isRemovePeopleFromGroupLoading,
      disableSecondaryActionButton: isRemovePeopleFromGroupLoading,
    }),
    [isRemovePeopleFromGroupLoading]
  );

  const hasMoreResults = loadMoreNumber > 0;

  const renderSearchToolbar = () => (
    <GroupPeopleSearchToolbar
      isLoading={isAddPeopleToGroupLoading}
      onAddPeopleClick={() => setIsAddPeopleVisible(true)}
      onToggleAllClick={toggleAll}
      isToggleAllChecked={!!numberOfSelectedItems}
      currentResults={resultsList.length}
      totalResults={totalResults}
      searchInputRef={searchInputRef}
      onSearchGroupInput={onSearchGroupInput}
      hideAddButton={isToolbarVisible}
    />
  );

  return (
    <>
      {isToolbarVisible && (
        <StickyToolbarContainer>{renderSearchToolbar()}</StickyToolbarContainer>
      )}
      <Table
        isLoading={
          searchStatus === SearchStatus.LOADING ||
          isAddPeopleToGroupLoading ||
          isRemovePeopleFromGroupLoading
        }
        hideHeader={isToolbarVisible}
        header={renderSearchToolbar}
        body={() =>
          totalResults > 0 ? (
            <>
              <Stack as="ul" className="removeListStyles">
                {resultsList.map((diner) => (
                  <Square
                    as="li"
                    inset="md"
                    key={diner.id}
                    borderBottom="1px solid"
                    borderColor="lightLine"
                  >
                    <Flex>
                      <Stack x="x-sm">
                        <Checkbox
                          id={`select-${diner.id}`}
                          name={`select-${diner.id}`}
                          ariaLabel={`select-${diner.id}`}
                          cancelLabelBottomMargin
                          checked={isSelected(diner.id)}
                          onChange={() => toggleItem(diner.id)}
                        />
                      </Stack>
                      <Stack flex={1}>
                        <Row alignItems="center">
                          <Column phone={10}>
                            <Row>
                              <Column desktop={4}>{diner.name}</Column>
                              <Column desktop={8}>{diner.email}</Column>
                            </Row>
                          </Column>
                          <Column phone={2}>
                            <Row justifyContent="flex-end">
                              <Button
                                variant="tertiary"
                                buttonSize="medium"
                                className="tableActionButtons"
                                onClick={() => setSelectedPersonToRemove(diner)}
                                aria-label={`Remove ${diner.name}`}
                              >
                                {isDesktop ? (
                                  'Remove'
                                ) : (
                                  <SvgX size="24px" aria-label="Remove" />
                                )}
                              </Button>
                            </Row>
                          </Column>
                        </Row>
                      </Stack>
                    </Flex>
                  </Square>
                ))}
              </Stack>
              {hasMoreResults && (
                <Squish inset="lg">
                  <Button
                    variant="secondary"
                    isLoading={searchStatus === SearchStatus.LOADING}
                    block
                    onClick={handleLoadMoreClick}
                  >
                    {searchStatus === SearchStatus.COMPLETE &&
                      `Load ${loadMoreNumber} more`}
                    {searchStatus === SearchStatus.ERROR && (
                      <Copy variant="body" color="primaryText">
                        Something went wrong loading your results.{' '}
                        <Copy variant="body" color="interactive">
                          Try again
                        </Copy>
                      </Copy>
                    )}
                  </Button>
                </Squish>
              )}
            </>
          ) : (
            <Square inset="lg">
              {hasNoResultsWithoutQuery && (
                <NoResultsMsg message="Add people so they can receive lines of credit for this group" />
              )}
              {hasNoResultsWithQuery && (
                <NoResultsMsg
                  message={`No results matching '${searchInputRef?.current?.value}'`}
                />
              )}
            </Square>
          )
        }
        footerMinHeight="60px"
        footer={() => (
          <Squish inset="lg" flex={1}>
            <Flex justifyContent="space-between" alignItems="center">
              <Column>
                <Copy variant="body">
                  {numberOfSelectedItems}{' '}
                  {numberOfSelectedItems === 1 ? ' person' : ' people'} selected
                </Copy>
              </Column>

              <Column phone={2}>
                <Button
                  variant="primary"
                  buttonSize="medium"
                  block
                  onClick={handleBulkRemoveClick}
                  aria-label="Remove selected people"
                >
                  Remove
                </Button>
              </Column>
            </Flex>
          </Squish>
        )}
        {...(isDesktop && {
          bodyMinHeight: '532px',
          bodyMaxHeight: '532px',
        })}
        showFooter={!!numberOfSelectedItems}
        isOverflowVisible={false}
      />
      {isAddPeopleVisible && (
        <AddPeopleToLocModal
          headerAndButtonText="Add people"
          onAdd={(dinersSelected) => {
            addPeopleToGroup(dinersSelected);
            setIsAddPeopleVisible(false);
          }}
          onClose={() => setIsAddPeopleVisible(false)}
          locId={groupId}
        />
      )}
      {isConfirmRemovePeopleVisible && (
        <SimpleDialog
          {...simpleDialogDefaultProps}
          title={`Remove ${numberOfSelectedItems} people?`}
          description="Once removed, they won't be associated with any credit lines given to this group."
          onSecondaryActionClick={() => {
            setIsConfirmRemovePeopleVisible(false);
          }}
          onPrimaryActionClick={() => {
            removePeopleFromGroup(selectedItems as string[], groupName);
          }}
        />
      )}
      {!!selectedPersonToRemove && (
        <SimpleDialog
          {...simpleDialogDefaultProps}
          title={`Remove ${selectedPersonToRemove.name}...?`}
          description="This person will also be removed from any lines of credit they may be assigned to"
          onSecondaryActionClick={() => {
            setSelectedPersonToRemove(null);
          }}
          onPrimaryActionClick={() => {
            removePeopleFromGroup(
              [selectedPersonToRemove.id],
              groupName,
              selectedPersonToRemove.name
            );
          }}
        />
      )}
    </>
  );
};
